import { useEffect, useState } from 'react'
import { HiOutlineSearch, HiXCircle } from 'react-icons/hi'
import { useShallow } from 'zustand/react/shallow'

import { AttributeKey, TelemetryData, Total } from '@ui/interfaces'
import { useAppSearchParams } from '@ui/lib/hooks/useAppSearchParams'
import { readableBytes, readableNumber } from '@ui/lib/utils'
import { useTelemetryStore } from '@ui/stores/explore'

interface GroupBySelectorProps {
  data: TelemetryData | undefined
  handleClose: () => void
}

export const GroupBySelector = ({ data, handleClose }: GroupBySelectorProps) => {
  const { searchParams, updateSearchParams } = useAppSearchParams()

  const [query, setQuery] = useState('')
  const [groupByData, setGroupByData] = useState<AttributeKey[]>([])
  const [total, setTotal] = useState<Total>({
    count: 0,
    size: 0,
  })

  const { dataSource, selectedKey, setSelectedKey } = useTelemetryStore(
    useShallow((state) => ({
      dataSource: state.dataSource,
      selectedKey: state.selectedKey,
      setSelectedKey: state.setSelectedKey,
    }))
  )

  useEffect(() => {
    if (data && data.telemetry?.uniqueKeys) {
      if (searchParams.get('groupBy')) {
        const selected = data.telemetry?.uniqueKeys?.find((key) => key.key === searchParams.get('groupBy'))
        if (selected) {
          setSelectedKey(selected)
        }
      }

      setGroupByData(data.telemetry.uniqueKeys || [])
      setTotal(data.telemetry.totals)
    }
  }, [data])

  const groupSortOrder = ['priority', 'resource', 'attributes', 'other']
  let priorityFilter: string[]
  if (dataSource === 'logs') {
    priorityFilter = ['resource.service.name', 'resource.host.name', 'body']
  } else {
    priorityFilter = ['resource.service.name', 'resource.host.name', 'name']
  }

  const organizeGroupData = (group: string) => {
    const data = query !== '' ? filterGroupData(query) : groupByData
    switch (group) {
      case 'priority':
        return data.filter((event) => priorityFilter.includes(event.key)).sort((a, b) => b.key.localeCompare(a.key))
      case 'resource':
        return data.filter((event) => event.key.startsWith('resource') && !priorityFilter.includes(event.key))
      case 'attributes':
        return data.filter((event) => event.key.startsWith('attributes') && !priorityFilter.includes(event.key))
      case 'other':
        return data.filter(
          (event) =>
            !event.key.startsWith('resource') &&
            !event.key.startsWith('attributes') &&
            !event.key.includes('timestamp') &&
            !priorityFilter.includes(event.key)
        )
      default:
        return []
    }
  }

  const filterGroupData = (query: string) => {
    return groupByData.filter((event) => event.key.toLowerCase().includes(query.toLowerCase()))
  }

  return (
    <div className="flex size-full flex-col rounded-sm bg-white">
      <div className="p-5">
        <p className="text-base font-semibold text-gray-900">Group events by</p>
        <div className="py-.5 mt-2 flex w-full items-center rounded-md border border-gray-300 bg-gray-50 px-2 shadow-sm focus:border-primary-300/50">
          <HiOutlineSearch className="size-5 text-gray-400 group-hover:text-indigo-500" />

          <input
            type="text"
            className="grow rounded-md border-none bg-gray-50 text-sm font-medium text-gray-900 focus:border-transparent focus:ring-0"
            placeholder="Search for a key"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />

          {query !== '' && (
            <HiXCircle
              className="size-6 cursor-pointer text-gray-600 group-hover:text-indigo-600"
              onClick={() => setQuery('')}
            />
          )}
        </div>
      </div>
      <div className="mt-2 h-full overflow-y-auto">
        {groupSortOrder.map((order: string, index) => (
          <div key={index} className="relative">
            <div className="sticky top-0 z-0 border-y border-b-gray-200 border-t-gray-100 bg-gray-50 px-5 py-1.5 text-sm font-semibold leading-6 text-gray-900">
              {order.charAt(0).toUpperCase() + order.slice(1)}
            </div>
            {organizeGroupData(order).map((group: AttributeKey) => (
              <div className="my-4 ml-5 flex items-center" key={group.key}>
                <input
                  id={group.key}
                  name="groupby"
                  type="radio"
                  className="without-ring border-gray-300 text-primary-600 hover:cursor-pointer"
                  checked={group.key === selectedKey.key}
                  onChange={() => {
                    updateSearchParams({ groupBy: group.key })
                    setSelectedKey(group)
                    handleClose()
                  }}
                />
                <label
                  htmlFor={group.key}
                  className="overflow-hidden text-ellipsis pl-2 text-sm hover:cursor-pointer"
                  title={group.key}>
                  {group.key}
                  <p id="helper-radio-text-4" className="text-xs font-normal text-gray-500">
                    {readableNumber(group.count)} events ({((group.count / total?.count) * 100).toFixed(1)}%) |{' '}
                    {readableBytes(group.size)}
                  </p>
                </label>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  )
}
